import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge, Card, StatCard } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Whether speech-to-text is actually working.
 *
 * This page used to print "spoke but received no transcript — check
 * speech-to-text" on dozens of calls at once, which is not a diagnosis. It
 * names the subsystem and hands the question back, and the person reading it
 * has no more idea than before whether the vendor is down, the key is wrong,
 * or nobody said anything.
 *
 * All three of those look identical from the call row and completely different
 * from the error log, and the two were never read together. So this is the
 * verdict rather than the symptom: the errors, the calls, and which errors
 * belong to which calls.
 *
 * The distinction that matters most is the dullest one. A call with no
 * transcript because the line was dead is not a fault — it is a list quality
 * problem wearing a fault's clothes, and treating it as one sends somebody to
 * go and restart a working service.
 */

interface Example {
  call_id: string | null;
  at: string;
  kind: string;
  message: string;
}

interface Health {
  status: string;
  provider: string;
  model: string;
  window_hours: number;
  calls_analyzed: number;
  calls_with_transcripts: number;
  calls_without_transcripts: number;
  stt_errors: number;
  provider_errors: number;
  configuration_errors: number;
  connection_errors: number;
  calls_affected: number;
  error_rate_pct: number;
  no_speech: number;
  unexplained: number;
  correlation: string;
  diagnosis: string;
  action: string;
  examples: Example[];
}

// HEALTHY is green and everything else is not, but "not healthy" covers a
// vendor blip and a key that has never worked, and those do not deserve the
// same colour. Amber is "watch it", red is "somebody has to do something".
const TONE: Record<string, 'green' | 'amber' | 'red'> = {
  HEALTHY: 'green',
  DEGRADED: 'amber',
  'NO SPEECH': 'amber',
  'PROVIDER ERROR': 'red',
  'CONFIGURATION ERROR': 'red',
  'CONNECTION ERROR': 'red',
  'NO TRANSCRIPT — CAUSE UNKNOWN': 'red',
};

const RING: Record<string, string> = {
  green: 'border-green-300 bg-green-50/60 dark:border-green-900 dark:bg-green-950/30',
  amber: 'border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30',
  red: 'border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30',
};

// Said in full rather than abbreviated: somebody reading this at 2am should
// not have to already know what "partial" means.
const CORRELATION: Record<string, string> = {
  none: 'No error could be tied to a specific call.',
  partial: 'Some errors were tied to a specific call, some could not be.',
  full: 'Every error was tied to a specific call.',
  'not applicable': 'There were no errors to tie to a call.',
};

export default function SttHealth({ hours }: { hours: number }) {
  const health = useQuery({
    queryKey: ['stt-health', hours],
    queryFn: () => api.get<Health>('/admin/stt-health', { hours }),
    refetchInterval: 60_000,
  });

  if (health.isPending || !health.data) return null;

  const h = health.data;
  const tone = TONE[h.status] ?? 'red';

  return (
    <Card
      title="Speech to text"
      description={`${h.provider}${h.model ? ` · ${h.model}` : ''} — the last ${h.window_hours} hours.`}
    >
      <div className={`rounded-lg border p-4 ${RING[tone]}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{h.status}</Badge>
          <span className="text-xs text-ink-500">
            {h.calls_analyzed} answered {h.calls_analyzed === 1 ? 'call' : 'calls'}{' '}
            examined
          </span>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-ink-800 dark:text-ink-100">
          {h.diagnosis}
        </p>

        {h.action && (
          <p className="mt-2 border-t border-black/5 pt-2 text-sm leading-relaxed text-ink-700 dark:border-white/10 dark:text-ink-300">
            <span className="font-medium">What to do: </span>
            {h.action}
          </p>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Calls with a transcript" value={String(h.calls_with_transcripts)} dense />
        <StatCard
          label="Calls without one"
          value={String(h.calls_without_transcripts)}
          dense
          tone={h.calls_without_transcripts > 0 ? 'warn' : 'default'}
        />
        <StatCard
          label="Nobody spoke"
          value={String(h.no_speech)}
          sub="Dead air, an answering machine, or a caller who said nothing. Not a fault."
          dense
        />
        <StatCard
          label="Unexplained"
          value={String(h.unexplained)}
          sub="Speech was heard, no transcript arrived, and no error was logged."
          dense
          tone={h.unexplained > 0 ? 'bad' : 'good'}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Errors logged" value={String(h.stt_errors)} dense />
        <StatCard
          label="Calls affected"
          value={String(h.calls_affected)}
          sub="One outage can hit many calls, and many errors can hit one."
          dense
        />
        <StatCard label="Error rate" value={`${h.error_rate_pct}%`} dense />
        <StatCard
          label="By kind"
          value={
            h.stt_errors === 0
              ? 'none'
              : [
                  h.provider_errors ? `${h.provider_errors} provider` : '',
                  h.configuration_errors ? `${h.configuration_errors} config` : '',
                  h.connection_errors ? `${h.connection_errors} connection` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
          dense
        />
      </div>

      <p className="mt-3 text-xs text-ink-500">
        {CORRELATION[h.correlation] ?? h.correlation}
      </p>

      {h.examples.length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
            Evidence
          </div>
          <ul className="mt-2 space-y-1.5">
            {h.examples.map((example, i) => (
              <li
                key={`${example.at}-${i}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs"
              >
                <span className="tabular-nums text-ink-500">
                  {formatDateTime(example.at)}
                </span>
                <Badge tone={TONE[example.kind] ?? 'red'}>{example.kind}</Badge>
                {example.call_id ? (
                  <Link
                    to={`/admin/calls/${example.call_id}`}
                    className="font-mono text-2xs text-brand-600 hover:underline dark:text-brand-400"
                  >
                    {example.call_id.slice(0, 8)}
                  </Link>
                ) : (
                  <span
                    className="text-2xs text-ink-400"
                    title="The error did not carry a call id, and more than one call was in flight."
                  >
                    no call
                  </span>
                )}
                <span className="text-ink-600 dark:text-ink-300">{example.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
